import React, { useState, useRef, useEffect } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import './Dashboard.css'; // Reuse existing styles

const BacktestingDashboard = () => {
  const [results, setResults] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [config, setConfig] = useState({
    assets: ['EURUSD', 'GBPUSD', 'USDJPY'],
    startDate: '2024-01-01',
    endDate: '2024-12-31',
    initialBalance: 10000,
    leverage: 10,
    maxPositionSize: 0.1,
    fees: 0.0001,
    spread: 0.00005,
    useAI: true,
    usePythonAI: true
  });

  const equityChartRef = useRef();
  const equityChartInstance = useRef();
  const equitySeriesRef = useRef();

  // Available assets for selection
  const availableAssets = [
    'EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'USDCHF',
    'NZDUSD', 'EURJPY', 'GBPJPY', 'AUDJPY', 'EURGBP', 'EURAUD'
  ];

  const runBacktest = async () => {
    setIsRunning(true);
    setResults(null);

    try {
      const response = await fetch('/api/backtest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config)
      });

      const data = await response.json();
      
      if (data.success) {
        setResults(data.results);
      } else {
        alert(`Backtest failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Backtest error:', error);
      alert(`Error running backtest: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  // Create equity curve chart
  useEffect(() => {
    if (!equityChartRef.current) return;
    
    equityChartInstance.current = createChart(equityChartRef.current, {
      width: 800,
      height: 400,
      layout: { background: { color: '#1e1e1e' }, textColor: '#ffffff' },
      grid: { vertLines: { color: '#444' }, horzLines: { color: '#444' } }
    });
    
    equitySeriesRef.current = equityChartInstance.current.addSeries(LineSeries, {
      color: '#027bff',
      lineWidth: 2
    });

    return () => {
      if (equityChartInstance.current) {
        equityChartInstance.current.remove();
      }
    };
  }, []);

  // Update equity chart when results change
  useEffect(() => {
    if (results && equitySeriesRef.current) {
      const chartData = results.equityCurve.map(point => ({
        time: point.time,
        value: point.equity
      }));
      equitySeriesRef.current.setData(chartData);
    }
  }, [results]);

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleAssetToggle = (asset) => {
    setConfig(prev => ({
      ...prev,
      assets: prev.assets.includes(asset)
        ? prev.assets.filter(a => a !== asset)
        : [...prev.assets, asset]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  return (
    <div style={{ padding: '20px', color: '#ffffff', backgroundColor: '#1e1e1e', minHeight: '100vh' }}>
      <h1 style={{ marginBottom: '30px' }}>Backtesting Dashboard</h1>

      {/* Configuration Panel */}
      <div style={{ display: 'flex', gap: '30px', marginBottom: '30px' }}>
        {/* Left Panel - Configuration */}
        <div style={{ flex: 1, backgroundColor: '#2a2a2a', padding: '20px', borderRadius: '8px' }}>
          <h2 style={{ marginBottom: '20px' }}>Backtest Configuration</h2>
          
          {/* Date Range */}
          <div style={{ marginBottom: '20px' }}>
            <h3>Date Range</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div>
                <label>Start Date:</label>
                <input
                  type="date"
                  value={config.startDate}
                  onChange={(e) => handleConfigChange('startDate', e.target.value)}
                  style={{ marginLeft: '10px', padding: '5px' }}
                />
              </div>
              <div>
                <label>End Date:</label>
                <input
                  type="date"
                  value={config.endDate}
                  onChange={(e) => handleConfigChange('endDate', e.target.value)}
                  style={{ marginLeft: '10px', padding: '5px' }}
                />
              </div>
            </div>
          </div>

          {/* Assets Selection */}
          <div style={{ marginBottom: '20px' }}>
            <h3>Assets to Trade</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
              {availableAssets.map(asset => (
                <label key={asset} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input
                    type="checkbox"
                    checked={config.assets.includes(asset)}
                    onChange={() => handleAssetToggle(asset)}
                  />
                  {asset}
                </label>
              ))}
            </div>
          </div>

          {/* Trading Parameters */}
          <div style={{ marginBottom: '20px' }}>
            <h3>Trading Parameters</h3>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={config.useAI}
                  onChange={(e) => handleConfigChange('useAI', e.target.checked)}
                />
                <span style={{ fontWeight: 'bold', color: config.useAI ? '#4caf50' : '#888' }}>
                  Use AI Trading Logic (Ensemble AI + Signal Analysis)
                </span>
              </label>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '5px', marginLeft: '30px' }}>
                {config.useAI ? 'Uses your live AI models for decision making' : 'Uses simple moving average crossover strategy'}
              </div>
              
              {config.useAI && (
                <div style={{ marginTop: '10px', marginLeft: '30px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      checked={config.usePythonAI}
                      onChange={(e) => handleConfigChange('usePythonAI', e.target.checked)}
                    />
                    <span style={{ fontWeight: 'bold', color: config.usePythonAI ? '#027bff' : '#888' }}>
                      Enable Python AI Models (Deep Learning + RL + Regime Detection)
                    </span>
                  </label>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '5px', marginLeft: '30px' }}>
                    {config.usePythonAI ? 'Adds advanced Python AI models to the ensemble' : 'Uses JavaScript AI models only'}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
              <div>
                <label>Initial Balance:</label>
                <input
                  type="number"
                  value={config.initialBalance}
                  onChange={(e) => handleConfigChange('initialBalance', Number(e.target.value))}
                  style={{ marginLeft: '10px', padding: '5px', width: '100px' }}
                />
              </div>
              <div>
                <label>Leverage:</label>
                <input
                  type="number"
                  value={config.leverage}
                  onChange={(e) => handleConfigChange('leverage', Number(e.target.value))}
                  style={{ marginLeft: '10px', padding: '5px', width: '100px' }}
                />
              </div>
              <div>
                <label>Max Position Size:</label>
                <input
                  type="number"
                  step="0.01"
                  value={config.maxPositionSize}
                  onChange={(e) => handleConfigChange('maxPositionSize', Number(e.target.value))}
                  style={{ marginLeft: '10px', padding: '5px', width: '100px' }}
                />
              </div>
              <div>
                <label>Fees (%):</label>
                <input
                  type="number"
                  step="0.0001"
                  value={config.fees}
                  onChange={(e) => handleConfigChange('fees', Number(e.target.value))}
                  style={{ marginLeft: '10px', padding: '5px', width: '100px' }}
                />
              </div>
            </div>
          </div>

          <button
            onClick={runBacktest}
            disabled={isRunning || config.assets.length === 0}
            style={{
              backgroundColor: isRunning ? '#666' : '#027bff',
              color: '#fff',
              border: 'none',
              padding: '15px 30px',
              borderRadius: '5px',
              fontSize: '16px',
              cursor: isRunning ? 'not-allowed' : 'pointer',
              width: '100%'
            }}
          >
            {isRunning ? 'Running Backtest...' : 'Run Backtest'}
          </button>
        </div>

        {/* Right Panel - Quick Stats */}
        {results && (
          <div style={{ flex: 1, backgroundColor: '#2a2a2a', padding: '20px', borderRadius: '8px' }}>
            <h2 style={{ marginBottom: '20px' }}>Performance Summary</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
              <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                <div style={{ fontSize: '14px', color: '#888' }}>Total Return</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: results.summary.totalReturn >= 0 ? '#4caf50' : '#f44336' }}>
                  {formatPercentage(results.summary.totalReturn)}
                </div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                <div style={{ fontSize: '14px', color: '#888' }}>Final Balance</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {formatCurrency(results.summary.finalBalance)}
                </div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                <div style={{ fontSize: '14px', color: '#888' }}>Win Rate</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {results.summary.winRate.toFixed(1)}%
                </div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                <div style={{ fontSize: '14px', color: '#888' }}>Total Trades</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {results.summary.totalTrades}
                </div>
              </div>
              {results.summary.useAI && results.summary.aiTrades > 0 && (
                <>
                  <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                    <div style={{ fontSize: '14px', color: '#888' }}>AI Trades</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#027bff' }}>
                      {results.summary.aiTrades}
                    </div>
                  </div>
                  <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                    <div style={{ fontSize: '14px', color: '#888' }}>AI Win Rate</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#027bff' }}>
                      {results.summary.aiWinRate.toFixed(1)}%
                    </div>
                  </div>
                </>
              )}
              
              {results.summary.usePythonAI && results.summary.pythonAITrades > 0 && (
                <>
                  <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                    <div style={{ fontSize: '14px', color: '#888' }}>Python AI Trades</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#9c27b0' }}>
                      {results.summary.pythonAITrades}
                    </div>
                  </div>
                  <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                    <div style={{ fontSize: '14px', color: '#888' }}>Python AI Win Rate</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#9c27b0' }}>
                      {results.summary.pythonAIWinRate.toFixed(1)}%
                    </div>
                  </div>
                </>
              )}
              <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                <div style={{ fontSize: '14px', color: '#888' }}>Profit Factor</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {results.summary.profitFactor.toFixed(2)}
                </div>
              </div>
              <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                <div style={{ fontSize: '14px', color: '#888' }}>Max Drawdown</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f44336' }}>
                  {formatPercentage(-Math.abs(results.summary.maxDrawdown))}
                </div>
              </div>
            </div>
            
            {/* Strategy Info */}
            <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
              <div style={{ fontSize: '14px', color: '#888' }}>Strategy Used</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: results.summary.useAI ? '#4caf50' : '#888' }}>
                {results.summary.useAI 
                  ? (results.summary.usePythonAI ? 'Hybrid AI (JS + Python)' : 'JavaScript AI Only')
                  : 'Simple Moving Average'
                }
              </div>
              {results.summary.useAI && results.summary.usePythonAI && (
                <div style={{ fontSize: '12px', color: '#9c27b0', marginTop: '5px' }}>
                  Includes Deep Learning, Reinforcement Learning & Regime Detection
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Results Section */}
      {results && (
        <div>
          {/* Equity Curve */}
          <div style={{ backgroundColor: '#2a2a2a', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <h2 style={{ marginBottom: '20px' }}>Equity Curve</h2>
            <div ref={equityChartRef} style={{ width: '100%', height: '400px' }} />
          </div>

          {/* Detailed Metrics */}
          <div style={{ backgroundColor: '#2a2a2a', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <h2 style={{ marginBottom: '20px' }}>Detailed Metrics</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '14px', color: '#888' }}>Average Win</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#4caf50' }}>
                  {formatCurrency(results.summary.avgWin)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '14px', color: '#888' }}>Average Loss</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#f44336' }}>
                  {formatCurrency(results.summary.avgLoss)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '14px', color: '#888' }}>Sharpe Ratio</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                  {results.summary.sharpeRatio.toFixed(3)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '14px', color: '#888' }}>Profit Factor</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                  {results.summary.profitFactor.toFixed(2)}
                </div>
              </div>
            </div>
            
            {/* AI Analytics */}
            {results.aiAnalytics && (
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#027bff' }}>AI Performance Breakdown</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                  <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                    <div style={{ fontSize: '12px', color: '#888' }}>Total AI Decisions</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#027bff' }}>
                      {results.aiAnalytics.totalAITrades}
                    </div>
                  </div>
                  <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                    <div style={{ fontSize: '12px', color: '#888' }}>Python AI Decisions</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#9c27b0' }}>
                      {results.aiAnalytics.pythonAITrades}
                    </div>
                  </div>
                  <div style={{ padding: '10px', backgroundColor: '#1e1e1e', borderRadius: '5px' }}>
                    <div style={{ fontSize: '12px', color: '#888' }}>Hybrid Decisions</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#4caf50' }}>
                      {results.aiAnalytics.hybridDecisions}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Recent Trades */}
          <div style={{ backgroundColor: '#2a2a2a', padding: '20px', borderRadius: '8px' }}>
            <h2 style={{ marginBottom: '20px' }}>Recent Trades (Last 20)</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #444' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Asset</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Direction</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Entry</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Exit</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>P&L</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Duration</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Reason</th>
                    {results.summary.useAI && <th style={{ padding: '10px', textAlign: 'left' }}>AI Confidence</th>}
                    {results.summary.usePythonAI && <th style={{ padding: '10px', textAlign: 'left' }}>Enhancement</th>}
                  </tr>
                </thead>
                <tbody>
                  {results.trades.slice(-20).reverse().map((trade, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #333' }}>
                      <td style={{ padding: '10px' }}>{trade.asset}</td>
                      <td style={{ padding: '10px', color: trade.direction === 'long' ? '#4caf50' : '#f44336' }}>
                        {trade.direction.toUpperCase()}
                      </td>
                      <td style={{ padding: '10px' }}>{trade.entryPrice.toFixed(5)}</td>
                      <td style={{ padding: '10px' }}>{trade.exitPrice.toFixed(5)}</td>
                      <td style={{ padding: '10px', color: trade.netPnL >= 0 ? '#4caf50' : '#f44336' }}>
                        {formatCurrency(trade.netPnL)}
                      </td>
                      <td style={{ padding: '10px' }}>
                        {Math.round(trade.duration / 60)} min
                      </td>
                      <td style={{ padding: '10px', fontSize: '12px' }}>{trade.reason}</td>
                      {results.summary.useAI && (
                        <td style={{ padding: '10px', fontSize: '12px', color: '#027bff' }}>
                          {trade.aiDecision ? `${(trade.aiDecision.confidence * 100).toFixed(1)}%` : 'N/A'}
                        </td>
                      )}
                      {results.summary.usePythonAI && (
                        <td style={{ padding: '10px', fontSize: '12px', color: '#9c27b0' }}>
                          {trade.enhancementReason || 'N/A'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BacktestingDashboard; 